import { useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, Space, Spin, Switch, Tabs, Tree, TreeSelect } from 'antd';
import type { TabsProps } from 'antd';
import { ContactsOutlined } from '@ant-design/icons';
import { Box, Grid, Typography } from '@mui/material';
import SweetAlert from 'sweetalert2';
import axios from 'axios';
import { useAuthHeader } from '../../../Services/Auth/sessionAuth';
import Data from '../../../Data/Data.json';
import { Crypter } from '../../../Functions/Security/Crypter';
import FONCTIONNALITE from '../../../Models/Fonctionnalite';

const ApiUrl = Data.ApiUrl;

interface TreeNode {
  key: string;
  title: React.ReactNode;
  value: string;
  children?: TreeNode[];
}

const Fonctionnalite = () => {
  const authHeader = useAuthHeader();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<FONCTIONNALITE[]>([]);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [mode, setMode] = useState<'create' | 'update' | 'none'>('none');

  const [ajoutActve, setAjoutActif] = useState(false);
  const [modifActve, setModifActve] = useState(true);
  const [enregActve, setEnregActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [actuActive, setActActive] = useState(false);
  const [annulActive, setAnnulActive] = useState(true);

  const buildTree = (items: FONCTIONNALITE[]): TreeNode[] => {
    const build = (parentCode: string | null): TreeNode[] => {
      return items
        .filter(i => (!parentCode && !i.LEPERE) || (i.LEPERE === parentCode))
        .sort((a, b) => (a.OrderIndex || 0) - (b.OrderIndex || 0))
        .map(i => ({
          key: i.CODMEN,
          value: i.CODMEN,
          title: (
            <span>
              <b>{i.LIBELMENFR}</b>
              {i.ICON ? <span style={{ color: 'gray', marginLeft: 8 }}>({i.ICON})</span> : null}
              {i.ACTIF === false ? <span style={{ color: 'red', marginLeft: 8 }}>[Inactif]</span> : null}
            </span>
          ),
          children: build(i.CODMEN)
        }));
    };
    return build(null);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const cypher = Crypter.encrypt(JSON.stringify({ Action: 'LIRE' }));
      const res = await axios.get(ApiUrl.ReadFonctionnalite, {
        headers: { 'CypherFonctionnalite': cypher, Authorization: authHeader(), 'Content-Type': 'application/json' }
      });
      const decrypted = JSON.parse(Crypter.decrypt(res.data)).Value;
      if (decrypted.StatutCode === 200) {
        const list: FONCTIONNALITE[] = decrypted.Result.Table || [];
        setData(list);
        setTreeData(buildTree(list));
      } else {
        SweetAlert.fire({ title: 'Erreur', text: decrypted.Message, icon: 'error' });
      }
    } catch (ex: any) {
      SweetAlert.fire({ title: 'Erreur', text: ex.message, icon: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  function initialiazeButton() {
    setAjoutActif(false);
    setModifActve(true);
    setEnregActve(true);
    setSupActive(true);
    setActActive(false);
    setAnnulActive(true);
  }

  const SetStart = () => {
    setMode('create');
    form.resetFields();
    const parent = data.find(d => d.CODMEN === selectedKey);
    form.setFieldsValue({
      CODMEN: '',
      LIBELMENFR: '',
      LIBELMENAN: '',
      LEPERE: selectedKey || null,
      NIVEAU: parent ? (parent.NIVEAU || 0) + 1 : 0,
      LIEN: '',
      ICON: '',
      OrderIndex: 1,
      ACTIF: true
    });
    setAjoutActif(true);
    setEnregActve(false);
    setModifActve(true);
    setSupActive(true);
    setAnnulActive(false);
    setActActive(true);
  };

  const modif = () => {
    if (!selectedKey) {
      SweetAlert.fire({ title: 'Attention', text: 'Veuillez sélectionner une fonctionnalité', icon: 'warning' });
      return;
    }
    setMode('update');
    setAjoutActif(true);
    setEnregActve(false);
    setModifActve(true);
    setSupActive(true);
    setAnnulActive(false);
    setActActive(true);
  };

  const SetEndStart = () => {
    if (!selectedKey) return;
    const item = data.find(d => d.CODMEN === selectedKey);
    if (!item) return;
    SweetAlert.fire({
      title: 'Suppression',
      text: `Voulez-vous supprimer ${item.LIBELMENFR} ?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: 'green',
      cancelButtonColor: 'darkred',
      confirmButtonText: 'Oui'
    }).then((r) => {
      if (r.isConfirmed) execute({ ...item, Action: 'SUPPRIMER' }, ApiUrl.SupprimerFonctionnalite, 'post');
    });
  };

  function handleAnulle() {
    form.resetFields();
    setSelectedKey(null);
    setMode('none');
    initialiazeButton();
    loadData();
  }

  const handleActiver = () => executeAction('ACTIVER', ApiUrl.ActiverFonctionnalite);
  const handleDesactiver = () => executeAction('DESACTIVER', ApiUrl.DesactiverFonctionnalite);

  const executeAction = (action: string, url: string) => {
    if (!selectedKey) return;
    const item = data.find(d => d.CODMEN === selectedKey);
    if (!item) return;
    execute({ ...item, Action: action }, url, 'post');
  };

  const execute = async (payload: FONCTIONNALITE, url: string, method: 'post' | 'put' | 'get') => {
    setLoading(true);
    try {
      const body = Crypter.encrypt(JSON.stringify(payload));
      const res = await axios.request({
        method,
        url,
        headers: { Authorization: authHeader(), 'Content-Type': 'application/json' },
        data: body
      });
      const decrypted = JSON.parse(Crypter.decrypt(res.data)).Value;
      if (decrypted.StatutCode === 200) {
        SweetAlert.fire({ title: 'Succès', text: decrypted.Message, icon: 'success' });
        form.resetFields();
        setSelectedKey(null);
        setMode('none');
        initialiazeButton();
        await loadData();
      } else {
        SweetAlert.fire({ title: 'Erreur', text: decrypted.Message, icon: 'error' });
      }
    } catch (ex: any) {
      SweetAlert.fire({ title: 'Erreur', text: ex.message, icon: 'error' });
    }
    setLoading(false);
  };

  const onFinish = async (values: any) => {
    const parent = data.find(d => d.CODMEN === values.LEPERE);
    const current = mode === 'update' ? data.find(d => d.CODMEN === selectedKey) : null;
    const payload: FONCTIONNALITE = {
      ...values,
      Id: current?.Id,
      LEPERE: values.LEPERE || null,
      NIVEAU: parent ? (parent.NIVEAU || 0) + 1 : 0,
      ACTIF: values.ACTIF === true
    };
    const url = mode === 'create' ? ApiUrl.CreateFonctionnalite : ApiUrl.UpdateFonctionnalite;
    await execute(payload, url, 'post');
  };

  const onFinishFailed = (error: any) => {
    const errors = error.errorFields.map((e: any) => e.name).join(' - ');
    SweetAlert.fire({ title: 'Champs requis', text: errors, icon: 'warning' });
  };

  const onSelect = (selectedKeys: any) => {
    const key = selectedKeys[0];
    setSelectedKey(key || null);
    if (key && !ajoutActve) {
      const item = data.find(d => d.CODMEN === key);
      if (item) {
        form.setFieldsValue({
          ...item,
          ACTIF: item.ACTIF !== false
        });
        setModifActve(false);
        setSupActive(false);
        setMode('none');
      }
    }
  };

  const titleRender = (nodeData: any) => nodeData.title;

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span id='Tab'>
          <ContactsOutlined />
          Gestion des fonctionnalités
        </span>
      ),
      children: (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Box className='bordure1' style={{ height: 'calc(100vh - 156px)', overflow: 'auto', padding: 16 }}>
              <Tree
                showLine
                onSelect={onSelect}
                treeData={treeData}
                titleRender={titleRender}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box className='bordure1' style={{ padding: 16 }}>
              <Form.Item name='CODMEN' label='Code Menu' rules={[{ required: true, message: '' }]}>
                <Input disabled={mode === 'update'} style={{ textTransform: 'uppercase' }} />
              </Form.Item>
              <Form.Item name='LIBELMENFR' label='Libellé FR' rules={[{ required: true, message: '' }]}>
                <Input />
              </Form.Item>
              <Form.Item name='LIBELMENAN' label='Libellé EN'>
                <Input />
              </Form.Item>
              <Form.Item name='LEPERE' label='Parent'>
                <TreeSelect
                  allowClear
                  treeData={treeData}
                  placeholder='Sélectionner le parent'
                />
              </Form.Item>
              <Form.Item name='NIVEAU' label='Niveau'>
                <InputNumber disabled min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name='LIEN' label='Lien'>
                <Input />
              </Form.Item>
              <Form.Item name='ICON' label='Icône'>
                <Input />
              </Form.Item>
              <Form.Item name='OrderIndex' label='Ordre' rules={[{ required: true, message: '' }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name='ACTIF' label='Actif' valuePropName='checked' initialValue={true}>
                <Switch checkedChildren='OUI' unCheckedChildren='NON' />
              </Form.Item>
            </Box>
          </Grid>
        </Grid>
      )
    }
  ];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'><b>GESTION DES FONCTIONNALITÉS</b></Typography>
      <Spin spinning={loading} size='large' style={{ color: 'green' }}>
        <Form disabled={!ajoutActve} autoComplete='off' labelAlign='left' form={form} name='dynamic_form_complex' onFinish={onFinish} onFinishFailed={onFinishFailed} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className='bG'>
            <legend><Typography color={'darkgreen'}>Actions</Typography></legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction='vertical' style={{ width: '100%' }}>
                <Button size='large' className='bt_add' disabled={ajoutActve} onClick={SetStart} type='ghost' htmlType='button' block>Ajouter</Button>
                <Button size='large' className='bt_add' disabled={modifActve} onClick={modif} type='ghost' htmlType='button' block>Modifier</Button>
                <Button size='large' className='bt_add' disabled={enregActve} type='ghost' htmlType='submit' block>Enregistrer</Button>
                <Button size='large' className='bt_add' onClick={SetEndStart} disabled={supActive} type='ghost' htmlType='button' block>Supprimer</Button>
                <Button size='large' className='bt_add' onClick={handleActiver} disabled={!selectedKey || ajoutActve} type='ghost' htmlType='button' block>Activer</Button>
                <Button size='large' className='bt_add' onClick={handleDesactiver} disabled={!selectedKey || ajoutActve} type='ghost' htmlType='button' block>Désactiver</Button>
                <Button size='large' className='bt_add' disabled={actuActive} onClick={loadData} type='ghost' htmlType='button' block>Actualiser</Button>
                <Button size='large' disabled={annulActive} onClick={handleAnulle} className='bt_add' type='ghost' htmlType='reset' block>Annuler</Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP' style={{width: "88%"}}>
            <div>
              <Tabs size='large' type='card' defaultActiveKey='1' items={items} />
            </div>
            <div>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default Fonctionnalite;